import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../index.css';

function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // CAPTCHA state
    const [captcha, setCaptcha] = useState({ question: '', answer: 0 });
    const [captchaInput, setCaptchaInput] = useState('');
    const [captchaError, setCaptchaError] = useState('');

    const { login } = useAuth();
    const navigate = useNavigate();

    const generateCaptcha = useCallback((keepError = false) => {
        const a = Math.floor(Math.random() * 10) + 1;
        const b = Math.floor(Math.random() * 10) + 1;
        const useAdd = Math.random() > 0.5;
        const question = useAdd ? `${a} + ${b}` : `${Math.max(a, b)} - ${Math.min(a, b)}`;
        const answer = useAdd ? a + b : Math.max(a, b) - Math.min(a, b);
        setCaptcha({ question, answer });
        setCaptchaInput('');
        if (!keepError) {
            setCaptchaError('');
        }
    }, []);

    useEffect(() => {
        generateCaptcha();
    }, [generateCaptcha]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setCaptchaError('');

        // Validate CAPTCHA before calling the server
        if (parseInt(captchaInput, 10) !== captcha.answer) {
            setCaptchaError('Incorrect security answer. Please try again.');
            generateCaptcha(true);
            return;
        }

        setLoading(true);
        const result = await login(email, password);
        setLoading(false);

        if (result.success) {
            navigate('/');
        } else {
            setError(result.error);
            generateCaptcha(); // refresh CAPTCHA after failed login
        }
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-header">
                    <h1>e-con Systems</h1>
                    <p>Stock & Sales Order Management</p>
                </div>

                <form onSubmit={handleSubmit} className="login-form">
                    <h2>Sign In</h2>

                    {error && (
                        <div className="error-message">
                            {error}
                        </div>
                    )}

                    {captchaError && (
                        <div className="error-message">
                            {captchaError}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            placeholder="Enter your email"
                            disabled={loading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            placeholder="Enter your password"
                            disabled={loading}
                        />
                    </div>

                    {/* CAPTCHA */}
                    <div className="form-group">
                        <label>Security Check</label>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            background: '#f3f4f6',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            padding: '0.6rem 0.85rem',
                            marginBottom: '0.25rem'
                        }}>
                            <span style={{
                                fontWeight: '700',
                                fontSize: '1.05rem',
                                letterSpacing: '0.08em',
                                fontFamily: 'monospace',
                                color: '#1f2937',
                                userSelect: 'none',
                                minWidth: '80px'
                            }}>
                                {captcha.question} = ?
                            </span>
                            <input
                                type="number"
                                value={captchaInput}
                                onChange={(e) => { setCaptchaInput(e.target.value); setCaptchaError(''); }}
                                placeholder="Answer"
                                required
                                disabled={loading}
                                style={{
                                    flex: 1,
                                    padding: '0.4rem 0.6rem',
                                    border: captchaError ? '1px solid #ef4444' : '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    fontSize: '0.95rem',
                                    outline: 'none'
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => generateCaptcha(false)}
                                disabled={loading}
                                title="Get a new question"
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '1.1rem',
                                    color: '#6b7280',
                                    padding: '0.2rem'
                                }}
                            >
                                &#x21bb;
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        className="btn-primary login-button"
                        disabled={loading}
                    >
                        {loading ? 'Signing In...' : 'Sign In'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default LoginPage;
